/** @param {string} text */
function p(text) {
  return { type: "paragraph", html: `<p>${text}</p>` };
}

/** @param {string} text */
function quote(text) {
  return { type: "quote", text };
}

/** @param {string} imageKey @param {string} caption */
function fig(imageKey, caption) {
  return {
    type: "figure",
    imageKey,
    caption: `[ ${caption} ]`,
  };
}

/** @param {string} videoId @param {string} [caption] */
function yt(videoId, caption) {
  return {
    type: "youtube",
    videoId,
    ...(caption ? { caption } : {}),
  };
}

export const SCIENTIFIC_ART_OF_PM_META = {
  heroCaption: "[ Charles Darwin's desk, Down House ]",
  eyebrow: "TECHNOLOGY · FIELD NOTES",
  tags: ["product", "product-management", "data", "vision", "nokia"],
  authorBio:
    "Product leader, photographer, storyteller. Builds things by day, thinks out loud here the rest of the time.",
};

export const SCIENTIFIC_ART_OF_PM_BODY = [
  {
    type: "lead",
    text: "We all treat product management as a scientific discipline. Product management is an intellectual pursuit where one submerges one's self in data, experiments, processes, regulations and user experience to find the optimum solution to a problem.",
  },
  p(
    "However, there is an art to creating a product that people will appreciate (even, dare I say it, love) into the market. Product people come from all types of backgrounds: to be a good product manager one must have a good grasp of all the disciplines required to build successful products. Most importantly they all share an innate drive to make things consistently better."
  ),
  p(
    "Product people fall within a broad spectrum. At each end of the spectrum lies Data/Evidence and Vision: broadly speaking, the visionary seldom looks at the data to build a product proposition, while the data product managers want clear evidence before creating capability. There are merits and limitations to both approaches, but in the wrong product-space, each strategy can be detrimental. As a rule of thumb, if you are enhancing a well-established product/app (not reinventing the wheel), then analytical data is an excellent place to start."
  ),
  p("The misconception of data..."),
  p(
    "Very recently, a CTO told me that for her to implement three discovery screens in a mobile app, she needs empirical evidence that users want to see 'discovery' screens. Her solution was to create a 'button' that gives a new customer the option to 'discover' the app (when they open it for the first time). However, to save engineering resources, when a customer clicks on 'discover', the app will tell the user \"Sorry, we are working on it\"."
  ),
  p(
    "For me, this is ludicrous! There are several issues with this approach: primarily the notion of compromising customer experience to get questionable evidence is bonkers! Another is engineering dictating what can and cannot be done in any way is something I detest; I thought we all have moved on from that after 2007!"
  ),
  fig("scientific-art-pm-inline-1.png", "compromising experience for questionable evidence"),
  quote(
    "You've got to start with the customer experience and work back towards the technology - not the other way around — Steve Jobs"
  ),
  p(
    "Another was a time when I was a mid-level programme manager for Nokia. Nokia was the first to develop a touchscreen device (Nokia 7710, I believe). Whether Nokia should allocate further resources to build touchscreen devices was something the strategical teams were grappling with. The support and the effort needed to create a touch-enabled ecosystem was a mammoth task. It required fundamental changes to the well-established model (business and tech). For instance, Symbian OS, upon which all Nokia devices were operating, was the most popular mobile OS in the world at the time, and required refactoring/re-engineering."
  ),
  p(
    "It is never a good idea to ask customers for their view when you're building something new. Such practice is frequently promoted by leaders who have no hypothesis of the future. They are busy trying to build a career and not a product. There are many leaders from yesteryear who have highlighted the perils of customer feedback."
  ),
  fig("scientific-art-pm-inline-2.png", "building a career, not a product"),
  quote(
    "If I had asked people what they wanted, they would have said faster horses — Henry Ford"
  ),
  fig("scientific-art-pm-inline-3.png", "faster horses"),
  quote(
    "We don't ask consumers what they want. They don't know. Instead, we apply our brainpower to what they need, and will want, and make sure we are there ready — Akio Morita"
  ),
  p(
    "Nokia fell into this pitfall head first. Large budgets were assigned to gather global customer feedback about mobile phone usage and touchscreen capability. Questions were (deliberately or not) designed to get the answer the business wanted. And they did!"
  ),
  p(
    "Customers do not like touch screen phones yelled Nokia. They were not alone. BlackBerry and Microsoft exclaimed the same. Even after the launch of the iPhone, Steven Ballmer claimed that \"It does not appeal to business customers because it does not have a keyboard...\""
  ),
  yt("eywi0h_Y5_U", "Ballmer laughs at the iPhone"),
  p(
    "Soon after June 2007, I was sitting in Nokia trading meetings with excel sheets showing Nokia phone sales in key markets in a sea of red. By then, the platform was wholly engulfed in a blaze far too late to remedy."
  ),
  p(
    "The word \"data\" inevitably gives the speaker some gravitas. Questioning the misconceptions, biases and incoherent data-signals that drive deduction can be dangerous in a corporate environment. It is the best tool for mediocre managers to prove to the business that change is undesirable, prototypes and tests are wastes of effort, and we are future-proof as we are. Many a time, I was told: \"Why are we doing this?\" \"Our data tells us that our customers don't like it\" or even worse \"There is no data that says this is a good idea\"."
  ),
  p(
    "Customer feedback and opinions are not data nor evidence. They are subjective opinions that can not be replicated in a test environment. Users lie. Their vision of design and execution is inferior to that of a product team. It's far better to base your product enhancements on behavioural data captured through analytics."
  ),
  p(
    "A truly piercing product manager intuitively knows the art of reading data signals. They know that data-signals should not be taken at face value as evidence, but must be investigated further. On the surface, implicit rationales, hidden behaviour and lies give rise to signals presumed to be unequivocal. The reason why good product managers (at times discard such feedback) promote high-fidelity prototypes in real-life conditions is to overcome these unknowns."
  ),
  p(
    "Yes, this approach requires resources and effort, and mature product-led organisations innately embrace the challenge. Leadership in such organisations gives the product teams the resources and the freedom to implement the best user experience across all levels. They provide the product teams with the autonomy to change, test, fail, learn and improve."
  ),
  p(
    "Good product-people are change agents who explore their surroundings to enhance them. They postulate numerous permutations of the future and its implications on the present which informs the vision they build. They look to fail early and carry it as a badge of honour."
  ),
  p(
    "Product management or ownership is a scientific pursuit. However, one must have the courage to go against the prevailing trends, even when the 'data' tells you otherwise. Just like an artist playing with a piece of clay, their vision is seldom shared until the sculpture is finally revealed. At this point, everyone was always on your side, they knew you were right all along, and they could have done it much better if they wanted to."
  ),
  fig("scientific-art-pm-inline-4.png", "the sculpture finally revealed"),
  quote(
    "It's really hard to design products by focus groups. A lot of times, people don't know what they want until you show it to them. — Steve Jobs"
  ),
  yt("X0Hnaaqh6yA", "Will Ferrell, Kevin Hart and Bill Burr — the BS of Hollywood meetings"),
  p(
    "I want to make it clear that I am not suggesting that data and customer feedback must be discarded. It is paramount that a product owner has a keen ear."
  ),
];
